export const normalizeClientId = value => String(value ?? "").trim().toUpperCase();
export const normalizeSymbol = value => String(value ?? "").trim().toUpperCase()
    .replace(/^(MCX|NSE|NFO|BFO|BSE|CDS):\s*/, "");
export const normalizeStrategy = value => String(value ?? "").trim().toUpperCase() || "NO_STRATEGY";

export function getOrderSide(order) {
    const side = String(order.side ?? "").trim().toUpperCase();
    if (["1", "BUY", "B"].includes(side)) return "BUY";
    if (["-1", "2", "SELL", "S"].includes(side)) return "SELL";
    return null;
}

export function isPendingOrder(order) {
    const status = String(order.status ?? "").trim().toLowerCase();
    // Terminal status must win over a stale pending value from the other field.
    if (["filled", "traded", "complete", "completed", "cancelled", "canceled", "rejected", "expired"].includes(status)) return false;
    const statusNo = String(order.status_no ?? "").trim();
    if (statusNo && statusNo !== "6" && statusNo !== "4") return false;
    return statusNo === "6" || ["pending", "open", "trigger pending", "not sent"].includes(status);
}

const positionKey = item => JSON.stringify([
    normalizeClientId(item.clientId || item.client_id),
    normalizeSymbol(item.symbol),
    normalizeStrategy(item.strategy),
]);

export function buildPositionLookup(positions = []) {
    const lookup = new Map();
    (positions || []).forEach(position => {
        if (!normalizeClientId(position.clientId || position.client_id) || !normalizeSymbol(position.symbol)) return;
        const qty = Number(position.net_qty ?? position.qty ?? 0);
        if (!Number.isFinite(qty)) return;
        const key = positionKey(position);
        lookup.set(key, (lookup.get(key) || 0) + qty);
    });
    return lookup;
}

// Pledge+ treats an opposite-side pending order against the same position as SL/exit.
export function isPositionSLOrder(order, positionLookup) {
    if (!isPendingOrder(order)) return false;
    const netQty = positionLookup.get(positionKey(order)) || 0;
    const side = getOrderSide(order);
    return (netQty > 0 && side === "SELL") || (netQty < 0 && side === "BUY");
}

// Shared instrument scope for the Pledge+ table and its cancellation controls.
export function getPledgePlusAsset(symbol) {
    const upper = String(symbol ?? "").trim().toUpperCase();
    if (upper.endsWith("CE") || upper.endsWith("PE") || /\s(?:CE|PE)\s/.test(upper)) return null;
    if (!upper.includes("MCX") && !upper.includes("NFO") && !upper.includes("BFO") && !upper.endsWith("FUT")) return null;
    const clean = normalizeSymbol(upper);
    const knownAssets = [
        "CRUDEOIL", "GOLD", "SILVER", "NATURALGAS", "COPPER", "ZINC", "LEAD", "ALUMINIUM", "MENTHAOIL", "COTTON",
        "RELIANCE", "HDFCBANK", "INFY", "TCS", "SBIN",
    ];
    return knownAssets.find(asset => clean.startsWith(asset)) || clean.match(/^([A-Z]+)/)?.[1] || null;
}

export function isCancellablePledgePlusEntry(order, positionLookup) {
    return Boolean(getPledgePlusAsset(order.symbol) && getOrderSide(order) &&
        isPendingOrder(order) && !isPositionSLOrder(order, positionLookup));
}
